import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Info } from "lucide-react";
import { CLIENT_FIELD_OPTIONS, ClientFieldType } from "@/types/variable-mappings";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ClientFieldSelectorProps {
  selectedField?: ClientFieldType;
  onFieldChange: (field: ClientFieldType) => void;
}

export function ClientFieldSelector({ selectedField, onFieldChange }: ClientFieldSelectorProps) {
  // Obtener la opción seleccionada para mostrar descripción
  const selectedOption = CLIENT_FIELD_OPTIONS.find(opt => opt.value === selectedField);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Campo del Cliente
        </CardTitle>
        <CardDescription>
          Selecciona qué campo del cliente quieres renderizar en esta variable
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Información sobre resolución automática */}
        <Alert className="border-blue-200 bg-blue-50/50">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-900 text-sm">
            <strong>Resolución Automática:</strong> El cliente se identifica automáticamente
            desde la submission al generar el reporte (submission → lanzamiento → cliente).
            Una misma plantilla puede usarse con múltiples clientes.
          </AlertDescription>
        </Alert>

        {/* Selector de Campo */}
        <div className="space-y-2">
          <Label>Campo a Renderizar</Label>
          <Select value={selectedField} onValueChange={onFieldChange}>
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar campo del cliente" />
            </SelectTrigger>
            <SelectContent>
              {CLIENT_FIELD_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex flex-col">
                    <span className="font-medium">{option.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Preview del campo seleccionado */}
        {selectedOption && (
          <div className="rounded-lg border bg-muted/50 p-4">
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-1 flex-1">
                <p className="font-medium text-sm">{selectedOption.label}</p>
                <p className="text-xs text-muted-foreground">{selectedOption.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                    Tabla: clientes
                  </span>
                  <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                    {selectedOption.value}
                  </code>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ejemplo de uso */}
        <div className="rounded-lg border border-green-200 bg-green-50/50 p-3 space-y-2">
          <p className="text-sm font-medium text-green-900">Ejemplo de Uso:</p>
          <div className="text-xs text-green-900 space-y-1">
            <p>1. Selecciona el campo que deseas (ej: <code className="bg-green-100 px-1 rounded">nombre</code>)</p>
            <p>2. En tu plantilla PPT, usa el placeholder de la variable (ej: <code className="bg-green-100 px-1 rounded">{"{nombreCliente}"}</code>)</p>
            <p>3. Al generar el reporte, se reemplazará con el valor real del cliente</p>
          </div>
        </div>

        {/* Información adicional */}
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
          <p className="text-sm text-amber-900">
            <strong>Nota:</strong> Cada reporte obtiene los datos del cliente asociado al lanzamiento
            desde el cual se genera la submission. Esto permite que una misma plantilla funcione
            dinámicamente con múltiples clientes.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
